// DEV-07 通道設定總表：NVR / Station 的 CH1–CHn 並排，一列一個設定項目；跟多數通道不同的格子標出來，能改的格子直接改
//（寫那一路的 shadow desired、回讀到不再 pending 才算完成——設備 ack 後以回報值為準，跟寫入值不同標 adjusted）。按「載入」才讀（一路一個請求）。整批對齊用下面的「複製到其他通道」。
// 列上方「常用 / 進階 / 技術」篩選晶片（預設常用 + 進階；技術只給 owner / admin）；技術項、碼流類、模型帶 confirm 的改之前第 2 級確認。
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { setDesired } from '@kinio/web-sdk';
import { deviceApi, affectsStreams, confirmText, type DeviceSettings, type SettingItem } from '../../lib/deviceApi';
import type { VmsDevice } from '../../lib/devices';
import { enumText, itemLabel } from '../../lib/kinioLabel';
import { useConfirm } from '../../components/ConfirmDialog';
import { buildMatrix, cellEditable, filterMatrix, isOdd } from './matrix';
import { settingText, tierOf, type Tier } from './settingsTiers';
import { TierChips, defaultTierSet } from './TierChips';

type CellState = { state: 'pending' | 'applied' | 'adjusted' | 'failed'; err?: string; want?: unknown };

const READBACK_TRIES = 12;
const READBACK_MS = 2000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

function display(it: SettingItem): string {
  const v = it.value;
  if (v === undefined || v === null) return '—';
  if (typeof v === 'number' && it.presets?.some((p) => p.value === v)) return settingText(it, v);
  if (it.enum && (typeof v === 'number' || typeof v === 'string')) return enumText(it, v);
  if (typeof v === 'boolean') return v ? 'ON' : 'OFF';
  if (typeof v === 'object') return '{…}';
  return String(v);
}

export function ChannelMatrix({ dev, canTech = false }: { dev: VmsDevice; canTech?: boolean }) {
  const { t } = useTranslation();
  const chans = dev.children.filter((c) => c.channel !== null);
  const [data, setData] = useState<Record<string, DeviceSettings | undefined> | null>(null);
  const [busy, setBusy] = useState(false);
  const [onlyDiff, setOnlyDiff] = useState(false);
  const [cells, setCells] = useState<Record<string, CellState>>({});
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [tiers, setTiers] = useState<Set<Tier>>(defaultTierSet);
  const { ask, dialog } = useConfirm();
  if (chans.length < 2) return null;

  const read = (aid: string) => deviceApi.settings(dev.apiEndpoint, dev.homeId, aid);
  async function load() {
    setBusy(true);
    setCells({});
    const list = await Promise.all(chans.map(async (c) => [c.aid, await read(c.aid).catch(() => undefined)] as const));
    setData(Object.fromEntries(list));
    setBusy(false);
  }

  async function write(aid: string, it: SettingItem, value: unknown) {
    if (same(value, it.value)) return;
    const technical = tierOf(it) === 'technical';
    const streams = affectsStreams(it.key);
    const confirm = confirmText(it, value);
    if (technical || streams || confirm) {
      const label = `${chans.find((c) => c.aid === aid)?.name ?? ''} ${itemLabel(it)}`;
      const ok = await ask({
        title: streams ? t('dset.streams_title') : technical ? t('dset.tech_title') : confirm!,
        body: (
          <>
            <p>{t('dset.change_body', { label, value: settingText(it, value) })}</p>
            {confirm && (streams || technical) && <p>{confirm}</p>}
          </>
        ),
        action: t('dset.apply_change', { label }),
      });
      if (!ok) return;
    }
    const id = aid + '|' + it.key;
    const [cluster, attr] = it.key.split('.');
    setCells((c) => ({ ...c, [id]: { state: 'pending', want: value } }));
    try {
      await setDesired(dev.apiEndpoint, dev.homeId, aid, { [cluster]: { [attr]: value } }, 'vms-web');
      // pending = 該 key 還在 desired（設備還沒 ack）；清掉後 value 就是設備實際採用的（SPEC #shadow-sync）
      let done: CellState | null = null;
      for (let i = 0; i < READBACK_TRIES && !done; i++) {
        await sleep(READBACK_MS);
        const now = await read(aid);
        const cur = now.sections.flatMap((s) => s.clusters.flatMap((c) => c.items)).find((x) => x.key === it.key);
        if (cur && !cur.pending) {
          done = { state: same(cur.value, value) ? 'applied' : 'adjusted' };
          setData((d) => ({ ...(d ?? {}), [aid]: now }));
        }
      }
      setCells((c) => ({ ...c, [id]: done ?? { state: 'failed', err: t('dset.timeout') } }));
    } catch (e) {
      setCells((c) => ({ ...c, [id]: { state: 'failed', err: String((e as { code?: string }).code ?? (e as Error).message ?? e) } }));
    }
    setDraft((d) => {
      const n = { ...d };
      delete n[id];
      return n;
    });
  }

  const editor = (aid: string, name: string, it: SettingItem) => {
    const id = aid + '|' + it.key;
    const pending = cells[id]?.state === 'pending';
    const label = `${name} ${itemLabel(it)}`;
    const shown = pending ? cells[id]?.want : it.value; // 寫入中先顯示要寫的值，回讀後換成設備的值
    if (it.control === 'switch')
      return <input type="checkbox" checked={shown === true} disabled={pending} aria-label={label} onChange={(e) => void write(aid, it, e.target.checked)} />;
    if (it.control === 'select')
      return (
        <select
          className="input input-sm"
          value={shown === undefined || shown === null ? '' : String(shown)}
          disabled={pending}
          aria-label={label}
          onChange={(e) => void write(aid, it, Number(e.target.value))}
        >
          {it.value === undefined || it.value === null ? <option value="">—</option> : null}
          {/* 目前值不在檔位裡（例 240 秒）：顯示格式化後的值，但不當成可選的選項 */}
          {it.presets?.length && typeof shown === 'number' && !it.presets.some((p) => p.value === shown) ? (
            <option value={String(shown)} disabled hidden>
              {settingText(it, shown)}
            </option>
          ) : null}
          {it.presets?.length
            ? it.presets.map((p) => (
                <option key={p.value} value={String(p.value)}>
                  {settingText(it, p.value)}
                </option>
              ))
            : Object.keys(it.enum ?? {}).map((k) => (
                <option key={k} value={k}>
                  {enumText(it, k)}
                </option>
              ))}
        </select>
      );
    const num = it.control !== 'text';
    const cur = id in draft ? draft[id] : it.value === undefined || it.value === null ? '' : String(it.value);
    const next: unknown = num ? Number(cur) : cur;
    const changed = id in draft && !same(next, it.value);
    const bad =
      num &&
      (cur.trim() === '' || Number.isNaN(Number(cur)) || (it.min !== undefined && Number(cur) < it.min) || (it.max !== undefined && Number(cur) > it.max));
    return (
      <span className="row-inline">
        <input
          className="input input-sm"
          style={{ width: num ? 64 : 110 }}
          type={num ? 'number' : 'text'}
          min={it.min}
          max={it.max}
          value={cur}
          disabled={pending}
          aria-label={label}
          aria-invalid={changed && bad}
          onChange={(e) => setDraft((d) => ({ ...d, [id]: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && changed && !bad) void write(aid, it, next);
          }}
        />
        {changed && (
          <button className="btn btn-sm" disabled={pending || bad} aria-label={t('chmx.save_cell', { name: label })} onClick={() => void write(aid, it, next)}>
            ✓
          </button>
        )}
      </span>
    );
  };

  const sections = data
    ? filterMatrix(
        buildMatrix(
          chans.map((c) => c.aid),
          data,
        ),
        tiers,
      )
    : [];
  const unread = data ? chans.filter((c) => !data[c.aid]) : [];
  const diffCount = sections.reduce((n, s) => n + s.rows.filter((r) => r.differs).length, 0);
  return (
    <section className="card" data-testid="channel-matrix" style={{ margin: '16px 0' }}>
      <h3>{t('chmx.title')}</h3>
      <p className="muted small">{t('chmx.help')}</p>
      <div className="toolbar">
        <button className="btn" disabled={busy} onClick={() => void load()}>
          {busy ? t('common.loading') : data ? t('chmx.reload') : t('chmx.load', { count: chans.length })}
        </button>
        {data && (
          <>
            <TierChips value={tiers} onChange={setTiers} canTech={canTech} />
            <label className="row-inline">
              <input type="checkbox" checked={onlyDiff} onChange={(e) => setOnlyDiff(e.target.checked)} /> {t('chmx.only_diff')}
            </label>
            <span className="muted small" data-testid="chmx-diff">
              {t('chmx.diff_count', { n: diffCount })}
            </span>
          </>
        )}
      </div>
      {unread.length > 0 && <p className="error small">{t('chmx.unread', { list: unread.map((c) => c.name).join(t('common.list_sep')) })}</p>}
      {data && sections.length === 0 && <p className="muted">{t('dset.none')}</p>}
      {sections.length > 0 && (
        <div className="table-wrap" style={{ overflow: 'auto', maxHeight: 560 }}>
          <table className="grid chmx">
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, minWidth: 160 }}>{t('chmx.col_setting')}</th>
                {chans.map((c) => (
                  <th key={c.aid}>{c.name}</th>
                ))}
              </tr>
            </thead>
            {sections.map((s) => {
              const rows = s.rows.filter((r) => !onlyDiff || r.differs);
              if (!rows.length) return null;
              return (
                <tbody key={s.name} data-chmx-section={s.name}>
                  <tr>
                    <th colSpan={chans.length + 1} className="muted" style={{ textAlign: 'left' }}>
                      {s.label}
                    </th>
                  </tr>
                  {rows.map((r) => (
                    <tr key={r.key} data-chmx-row={r.key} data-differs={r.differs}>
                      <th scope="row" style={{ position: 'sticky', left: 0, textAlign: 'left', fontWeight: 400 }}>
                        {r.label}
                      </th>
                      {r.cells.map((c, i) => {
                        const aid = chans[i].aid;
                        const st = cells[aid + '|' + r.key];
                        return (
                          <td
                            key={aid}
                            data-chmx-cell={aid}
                            data-odd={isOdd(r, c)}
                            data-state={st?.state}
                            className={isOdd(r, c) ? 'chmx-odd' : undefined}
                            title={st?.err}
                          >
                            {!c ? (
                              <span className="muted">—</span>
                            ) : cellEditable(c) ? (
                              editor(aid, chans[i].name, c)
                            ) : c.control === 'custom' ? (
                              <Link className="small" to={`/devices/${encodeURIComponent(dev.homeId)}/${encodeURIComponent(aid)}?tab=settings`}>
                                {t('chmx.custom')}
                              </Link>
                            ) : (
                              <span className="mono">{display(c)}</span>
                            )}
                            {st && st.state !== 'applied' && <span className={`badge dset-${st.state}`}>{t(`dset.${st.state}`)}</span>}
                            {st?.state === 'applied' && <span className="muted small"> ✓</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              );
            })}
          </table>
        </div>
      )}
      {dialog}
    </section>
  );
}
